import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// ── Français (source) ────────────────────────────────────────────────
import frCommon from '../../../../shared/locales/fr/common.json';
import frAuth from '../../../../shared/locales/fr/auth.json';
import frLayout from './locales/fr/layout.json';
import frStock from './locales/fr/stock.json';
import frEquipe from './locales/fr/equipe.json';
import frRepondeur from './locales/fr/repondeur.json';
import frAdmin from './locales/fr/admin.json';
import frZilloteam from './locales/fr/zilloteam.json';
import frZillodocs from './locales/fr/zillodocs.json';
import frZillotasks from './locales/fr/zillotasks.json';
import frRedactia from './locales/fr/redactia.json';
import frTour from './locales/fr/tour.json';
import frZillotv from './locales/fr/zillotv.json';

// ── Română ───────────────────────────────────────────────────────────
import roCommon from '../../../../shared/locales/ro/common.json';
import roAuth from '../../../../shared/locales/ro/auth.json';
import roLayout from './locales/ro/layout.json';
import roStock from './locales/ro/stock.json';
import roEquipe from './locales/ro/equipe.json';
import roRepondeur from './locales/ro/repondeur.json';
import roAdmin from './locales/ro/admin.json';
import roZilloteam from './locales/ro/zilloteam.json';
import roZillodocs from './locales/ro/zillodocs.json';
import roZillotasks from './locales/ro/zillotasks.json';
import roRedactia from './locales/ro/redactia.json';
import roTour from './locales/ro/tour.json';
import roZillotv from './locales/ro/zillotv.json';

// ── English ──────────────────────────────────────────────────────────
import enCommon from '../../../../shared/locales/en/common.json';
import enAuth from '../../../../shared/locales/en/auth.json';
import enLayout from './locales/en/layout.json';
import enStock from './locales/en/stock.json';
import enEquipe from './locales/en/equipe.json';
import enRepondeur from './locales/en/repondeur.json';
import enAdmin from './locales/en/admin.json';
import enZilloteam from './locales/en/zilloteam.json';
import enZillodocs from './locales/en/zillodocs.json';
import enZillotasks from './locales/en/zillotasks.json';
import enRedactia from './locales/en/redactia.json';
import enTour from './locales/en/tour.json';
import enZillotv from './locales/en/zillotv.json';

// ── Deutsch ──────────────────────────────────────────────────────────
import deCommon from '../../../../shared/locales/de/common.json';
import deAuth from '../../../../shared/locales/de/auth.json';
import deLayout from './locales/de/layout.json';
import deStock from './locales/de/stock.json';
import deEquipe from './locales/de/equipe.json';
import deRepondeur from './locales/de/repondeur.json';
import deAdmin from './locales/de/admin.json';
import deZilloteam from './locales/de/zilloteam.json';
import deZillodocs from './locales/de/zillodocs.json';
import deZillotasks from './locales/de/zillotasks.json';
import deRedactia from './locales/de/redactia.json';
import deTour from './locales/de/tour.json';
import deZillotv from './locales/de/zillotv.json';

const ns = [
  'common', 'auth', 'layout',
  'stock', 'equipe', 'repondeur', 'admin',
  'zilloteam', 'zillodocs', 'zillotasks',
  'redactia', 'tour', 'zillotv',
] as const;
export type I18nNamespace = (typeof ns)[number];

i18n.use(initReactI18next).init({
  lng: localStorage.getItem('locale') ?? 'fr',
  fallbackLng: 'fr',
  defaultNS: 'common',
  ns,
  resources: {
    fr: {
      common: frCommon, auth: frAuth, layout: frLayout,
      stock: frStock, equipe: frEquipe, repondeur: frRepondeur, admin: frAdmin,
      zilloteam: frZilloteam, zillodocs: frZillodocs, zillotasks: frZillotasks,
      redactia: frRedactia, tour: frTour, zillotv: frZillotv,
    },
    ro: {
      common: roCommon, auth: roAuth, layout: roLayout,
      stock: roStock, equipe: roEquipe, repondeur: roRepondeur, admin: roAdmin,
      zilloteam: roZilloteam, zillodocs: roZillodocs, zillotasks: roZillotasks,
      redactia: roRedactia, tour: roTour, zillotv: roZillotv,
    },
    en: {
      common: enCommon, auth: enAuth, layout: enLayout,
      stock: enStock, equipe: enEquipe, repondeur: enRepondeur, admin: enAdmin,
      zilloteam: enZilloteam, zillodocs: enZillodocs, zillotasks: enZillotasks,
      redactia: enRedactia, tour: enTour, zillotv: enZillotv,
    },
    de: {
      common: deCommon, auth: deAuth, layout: deLayout,
      stock: deStock, equipe: deEquipe, repondeur: deRepondeur, admin: deAdmin,
      zilloteam: deZilloteam, zillodocs: deZillodocs, zillotasks: deZillotasks,
      redactia: deRedactia, tour: deTour, zillotv: deZillotv,
    },
  },
  interpolation: {
    escapeValue: false, // React handles XSS
    // {{currency}} dans les libellés (« Valeur ({{currency}}) ») — résolu
    // automatiquement, sans passer la variable à chaque appel de t().
    defaultVariables: { currency: '€' },
  },
});

export { i18n };
export default i18n;

/**
 * Met à jour le symbole utilisé par {{currency}} dans toutes les traductions.
 * Appelé par AuthContext quand la devise du cabinet ou la langue change.
 */
export function setCurrencySymbol(symbol: string) {
  const interpolation = (i18n.options.interpolation ??= {});
  interpolation.defaultVariables = { ...interpolation.defaultVariables, currency: symbol };
  i18n.services.interpolator.init(interpolation, true);
}

/** Helper: change language + persist in localStorage */
export function setLocale(lang: string) {
  i18n.changeLanguage(lang);
  localStorage.setItem('locale', lang);
}

export const SUPPORTED_LOCALES = [
  { code: 'fr', label: 'Français' },
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]['code'];
